import React from 'react';
import { InformationCircleIcon } from '@interstate/components/Icons/InformationCircleIcon';
import { Tooltip } from '@interstate/components/Tooltip';
import { useInterstateTheme } from '@interstate/components/InterstateThemeProvider';

export type FieldOverrideIndicatorProps = {
    enabled: boolean;
    show: boolean;
    message: string;
    testId: string;
    // Callers in a fixed grid cannot afford the reserved border and icon slot, which insets and narrows
    // the cell against its neighbours. Opting out keeps the wrapper element, so the field still
    // reconciles in place rather than remounting when the indicator appears.
    reserveSpace?: boolean;
    // The tooltip wrapper is a flex child, so by default the icon takes a column of its own, which
    // narrows the field and pulls its right edge in off the plain inputs beside it in a fixed grid.
    // Overlaying positions the icon out of flow at the top right — on the label row — so the field
    // keeps the full cell width. The icon then never occupies a column, lit or dormant, which makes
    // `reserveSpace` govern only the accent bar and its padding.
    overlayIcon?: boolean;
    // The tooltip is reachable only by focusing the tap target, so a message that a screen reader
    // needs to hear without being asked has to go through a live region as well. Opt-in: for the
    // override indicators the message restates a value the user just typed, and announcing every
    // one of those would talk over them.
    //
    // The region is mounted empty for the whole enabled lifetime and the message arrives as a later
    // mutation. A live region that enters the DOM already populated is not reliably spoken, so
    // rendering it only alongside the message would announce nothing.
    announce?: boolean;
    showTooltip?: boolean;
    children: React.ReactNode;
};

const wrapperBaseStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'flex-start',
    borderLeftWidth: '4px',
    borderLeftStyle: 'solid',
    paddingLeft: '8px'
};
const childrenStyle: React.CSSProperties = { flex: 1 };
const iconSlotStyle: React.CSSProperties = { display: 'inline-flex', justifyContent: 'center', width: '44px' };
const tapTargetStyle: React.CSSProperties = {
    ...iconSlotStyle,
    padding: 0,
    border: 'none',
    background: 'transparent',
    cursor: 'pointer'
};

const overlayWrapperStyle: React.CSSProperties = { position: 'relative' };
const overlayIconSlotStyle: React.CSSProperties = { position: 'absolute', top: 0, right: 0 };
const overlayTapTargetStyle: React.CSSProperties = { ...tapTargetStyle, justifyContent: 'flex-end' };

const collapsedWrapperStyle: React.CSSProperties = {
    ...wrapperBaseStyle,
    borderLeftWidth: 0,
    paddingLeft: 0,
    borderLeftColor: 'transparent'
};

const visuallyHiddenStyle: React.CSSProperties = {
    position: 'absolute',
    width: '1px',
    height: '1px',
    padding: 0,
    margin: '-1px',
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    whiteSpace: 'nowrap',
    border: 0
};

const FieldOverrideIndicator = ({
    enabled,
    show,
    message,
    testId,
    reserveSpace = true,
    overlayIcon = false,
    announce = false,
    showTooltip = true,
    children
}: FieldOverrideIndicatorProps) => {
    const theme = useInterstateTheme();
    const [announcement, setAnnouncement] = React.useState('');

    React.useEffect(() => {
        if (!announce) {
            // Cleared rather than left standing: state survives the region unmounting, so a stale message
            // would still be here on a flip back on and the region would mount already populated.
            setAnnouncement('');
            return undefined;
        }
        const timer = window.setTimeout(() => setAnnouncement(enabled && show ? message : ''), 0);
        return () => window.clearTimeout(timer);
    }, [announce, enabled, show, message]);

    // Not enabled (toggle off, or a field the indicator doesn't cover): render children only — no
    // wrapper, border, padding, or reserved slot. This is the path every user is on until F283308
    // ships, so it must add nothing to the layout.
    if (!enabled) {
        return <>{children}</>;
    }

    // One tree across both states rather than a return per state, so the live region keeps its
    // position and identity when `show` flips and the message reads as a mutation rather than a mount.
    //
    // Dormant: reserve the border + icon slot (transparent) so the field does not shift when the
    // indicator turns on within a toggle-on session. `reserveSpace: false` trades that away.
    //
    // Lit: `reserveSpace` no longer applies — the border and icon slot are the indicator, so the full
    // wrapper style applies and the field shifts. A caller opting out of the reserved slot is choosing
    // not to occupy grid space while dormant, and accepting that shift when the indicator fires.
    // `overlayIcon` narrows that shift to the accent bar, since the icon stays out of flow.
    const dormantWrapperStyle = reserveSpace ? { ...wrapperBaseStyle, borderLeftColor: 'transparent' } : collapsedWrapperStyle;
    const stateWrapperStyle = show ? { ...wrapperBaseStyle, borderLeftColor: theme.tokens.BaseColorGold500 } : dormantWrapperStyle;

    const indicatorIcon = (
        <Tooltip id={`${testId}-tooltip`} toolTipContent={message} position="bottom" size="large">
            <button
                type="button"
                data-testid={`${testId}-tap-target`}
                aria-label={message}
                style={overlayIcon ? overlayTapTargetStyle : tapTargetStyle}
            >
                <InformationCircleIcon data-testid={`${testId}-icon`} color={theme.tokens.BaseColorGold500} aria-hidden={true} />
            </button>
        </Tooltip>
    );

    const positionedIcon = overlayIcon ? <span style={overlayIconSlotStyle}>{indicatorIcon}</span> : indicatorIcon;
    const litIcon = showTooltip ? positionedIcon : null;
    const dormantIconSlot = showTooltip && !overlayIcon && reserveSpace ? <span style={iconSlotStyle} aria-hidden={true} /> : null;

    return (
        <div
            data-testid={show ? testId : undefined}
            style={overlayIcon ? { ...stateWrapperStyle, ...overlayWrapperStyle } : stateWrapperStyle}
        >
            <div style={childrenStyle}>{children}</div>
            {show ? litIcon : dormantIconSlot}
            {announce && (
                <span role="status" aria-live="polite" data-testid={`${testId}-announcement`} style={visuallyHiddenStyle}>
                    {announcement}
                </span>
            )}
        </div>
    );
};

export default FieldOverrideIndicator;
